import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import RaffleForm from './RaffleForm';

// Split Text Component
const SplitText = ({ children, mobileText, ...props }) => {
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const textToShow = isMobile ? mobileText : children;
  const words = textToShow.split(' ');

  return (
    <motion.span {...props} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      {words.map((word, wordIndex) => (
        <motion.span
          key={wordIndex}
          style={{
            display: 'inline-block',
            marginRight: wordIndex < words.length - 1 ? '0.25em' : '0',
            verticalAlign: 'middle'
          }}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: wordIndex * 0.1,
            ease: "easeOut"
          }}
          viewport={{ once: true }}
        >
          {word.split('').map((char, charIndex) => (
            <motion.span
              key={charIndex}
              style={{
                display: 'inline-block',
                verticalAlign: 'middle'
              }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: (wordIndex * 0.1) + (charIndex * 0.05),
                ease: "easeOut"
              }}
              viewport={{ once: true }}
            >
              {char}
            </motion.span>
          ))}
        </motion.span>
      ))}
    </motion.span>
  );
};

const ProductsSection = styled.section`
  padding: 5rem 2rem;
  background: rgba(255, 255, 255, 0.9);
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
    min-height: auto;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
  
  /* Add padding to show scroll hint */
  @media (min-width: 769px) {
    padding: 0 1rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  color: #333;
  margin-bottom: 3rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ProductsGrid = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 1.5rem;
  margin-top: 3rem;
  padding: 1rem 0;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  position: relative;
  scroll-snap-type: x mandatory;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
  

  
  @media (max-width: 768px) {
    gap: 1.2rem;
    margin-top: 2rem;
    padding: 0.5rem 0;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
    margin-top: 1.5rem;
  }
`;

const ProductCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 3px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 240px;
  max-width: 240px;
  flex-shrink: 0;
  scroll-snap-align: start;
  height: 420px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    border-radius: 15px;
    min-width: 220px;
    max-width: 220px;
    height: 400px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 12px;
    min-width: 200px;
    max-width: 200px;
    height: 380px;
  }
  
  &:hover {
    border-color: #ff6b6b;
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    
    @media (max-width: 768px) {
      transform: translateY(-5px);
      box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    }
  }
`;

const ProductImage = styled(motion.div)`
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 160px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    height: 140px;
    margin-bottom: 0.8rem;
  }
  
  @media (max-width: 480px) {
    height: 120px;
    margin-bottom: 0.6rem;
  }
  
  img {
    max-width: 120px;
    max-height: 120px;
    width: auto;
    height: auto;
    object-fit: contain;
    
    @media (max-width: 768px) {
      max-width: 100px;
      max-height: 100px;
    }
    
    @media (max-width: 480px) {
      max-width: 80px;
      max-height: 80px;
    }
  }
  
  /* Special styling for Bikini Chikin to make it larger */
  img[src*="transparentBikini"] {
    max-width: 140px;
    max-height: 140px;
    transform: scale(1.7);
    
    @media (max-width: 768px) {
      max-width: 120px;
      max-height: 120px;
      transform: scale(1.6);
    }
    
    @media (max-width: 480px) {
      max-width: 100px;
      max-height: 100px;
      transform: scale(1.5);
    }
  }
`;

const ProductTitle = styled.h3`
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 0.8rem;
  font-weight: bold;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0.6rem;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  }
`;

const ProductDescription = styled.p`
  color: #666;
  margin-bottom: 1rem;
  line-height: 1.4;
  font-size: 0.85rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  flex-grow: 1;
  min-height: 80px;
  display: flex;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 0.8rem;
    line-height: 1.3;
    min-height: 70px;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 0.6rem;
    min-height: 60px;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  }
`;

const ShopButton = styled(motion.a)`
  display: inline-block;
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  text-decoration: none;
  padding: 0.6rem 1.2rem;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  transition: all 0.3s ease;
  flex-shrink: 0;
  margin-top: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    width: 100%;
    text-align: center;
    display: block;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
    
    @media (max-width: 768px) {
      transform: translateY(-1px);
    }
  }
`;

const ScrollHint = styled.div`
  text-align: center;
  margin-top: 1rem;
  color: #666;
  font-size: 0.9rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const ScrollContainer = styled.div`
  position: relative;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    margin-top: 2rem;
  }
  
  @media (max-width: 480px) {
    margin-top: 1.5rem;
  }
`;

// aCAPTCHA Modal Components
const CaptchaOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CaptchaContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffd93d 0%, #ff6b6b 50%, #6bcf7f 100%);
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

const CaptchaTitle = styled(motion.h2)`
  color: white;
  font-size: 1.8rem;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const Products = () => {
  const [raffleFormOpen, setRaffleFormOpen] = useState(false);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [captchaText, setCaptchaText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [useFallbackCaptcha] = useState(false);
  const scrollContainerRef = useRef(null);

  // Handle aCAPTCHA events and script loading
  useEffect(() => {
    const handleCaptchaEvents = () => {
      const captchaElement = document.getElementById('airdrop-captcha');
      if (captchaElement) {
        captchaElement.addEventListener('verified', handleCaptchaVerified);
      }
    };

    if (captchaOpen) {
      // Check if aCAPTCHA script is loaded
      const checkScriptLoaded = () => {
        if (typeof window !== 'undefined' && window.customElements && window.customElements.get('captcha-element')) {
          // Script is loaded, set up events
          setTimeout(handleCaptchaEvents, 100);
        } else {
          // Script not loaded yet, try again
          setTimeout(checkScriptLoaded, 200);
        }
      };

      checkScriptLoaded();
    }

    return () => {
      const captchaElement = document.getElementById('airdrop-captcha');
      if (captchaElement) {
        captchaElement.removeEventListener('verified', handleCaptchaVerified);
      }
    };
  }, [captchaOpen]);

  const products = [
    {
      id: 1,
      name: "Chikin Lady",
      image: "/assets/transparentchikinladyLOGO-2.png",
      description: "The iconic Chikin Lady logo! Represent the brand with style and attitude.",
      category: "Brand",
      link: "https://thirdweb.com/base/0xC04530534be54e542689e7CD1d70961E2aDDD337",
      buttonLabel: "Purchase"
    },
    {
      id: 6,
      name: "Halloween Chikin",
      image: "/assets/transparentHalloween.png",
      description: "Spooky, sassy, and ready to haunt the roost! Brew up some coop magic with this bewitching seasonal drop.",
      category: "Seasonal",
      link: "https://thirdweb.com/base/0xC04530534be54e542689e7CD1d70961E2aDDD337",
      buttonLabel: "Purchase"
    },
    {
      id: 2,
      name: "Freakn' Chikin",
      image: "/assets/transparentFreakn'chikin.png",
      description: "The most badass chicken you'll ever meet! For those who dare to be different.",
      category: "Attitude",
      link: "https://thirdweb.com/base/0xC04530534be54e542689e7CD1d70961E2aDDD337",
      buttonLabel: "Purchase"
    },
    {
      id: 3,
      name: "Fried Chikin",
      image: "/assets/transparentFRIEDCHICKEN.png",
      description: "Our signature fried chicken design! Crispy, golden, and absolutely delicious.",
      category: "Classic",
      link: "https://thirdweb.com/base/0xC04530534be54e542689e7CD1d70961E2aDDD337",
      buttonLabel: "Purchase"
    },
    {
      id: 4,
      name: "Bandana Chikin",
      image: "/assets/transparentBandana.png",
      description: "Rock that bandana look with our sassy chicken design! Perfect for any occasion.",
      category: "Accessories",
      link: "https://thirdweb.com/base/0xC04530534be54e542689e7CD1d70961E2aDDD337",
      buttonLabel: "Purchase"
    },
    {
      id: 5,
      name: "Bikini Chikin",
      image: "/assets/transparentBikini.png",
      description: "Beach ready with our fabulous chicken in a bikini! Summer vibes all year round.",
      category: "Beachwear",
      link: "https://thirdweb.com/base/0xC04530534be54e542689e7CD1d70961E2aDDD337",
      buttonLabel: "Purchase"
    }
  ];

  // Generate random captcha text (fallback)
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Open captcha modal - function removed as it's not currently used
  // const handleAirdropClick = (e) => { ... };

  // Handle aCAPTCHA verification
  const handleCaptchaVerified = (event) => {
    const { verified } = event.detail;
    if (verified) {
      setCaptchaOpen(false);
      window.open('https://docs.google.com/forms/d/e/1FAIpQLSfaLddnUj835SocwEvpKPNnmR-gOTjUjnOVxxkXlemedzY3Xw/viewform?usp=sharing&ouid=108309184503602048460', '_blank');
    }
  };

  // Handle fallback captcha verification
  const handleFallbackCaptchaSubmit = () => {
    if (userInput.toUpperCase() === captchaText.toUpperCase()) {
      setCaptchaOpen(false);
      window.open('https://docs.google.com/forms/d/e/1FAIpQLSfaLddnUj835SocwEvpKPNnmR-gOTjUjnOVxxkXlemedzY3Xw/viewform?usp=sharing&ouid=108309184503602048460', '_blank');
    } else {
      setCaptchaError(true);
      const newCaptcha = generateCaptcha();
      setCaptchaText(newCaptcha);
      setUserInput('');
    }
  };

  // Close captcha modal
  const handleCaptchaClose = () => {
    setCaptchaOpen(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -260,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 260,
        behavior: 'smooth'
      });
    }
  };

  const handleShopClick = () => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-1047967012/9GnhCNaIzIAcEKTq2vMD'
      });
      console.log('Conversion tracking event sent (Shop Now).');
    }
  };

  return (
    <ProductsSection id="products">
      <Container>
        <SectionTitle>
          <img
            src="/assets/transparentchikinladyLOGO-2.png"
            alt="Chikin Lady"
            style={{
              height: '1.5em',
              width: 'auto',
              verticalAlign: 'middle',
              marginRight: '0.5rem',
              marginLeft: '0.5rem'
            }}
          />
          <SplitText mobileText="Our Cluckin' NFT's!">Our Cluckin' NFT Collection!</SplitText>
          <img
            src="/assets/transparentchikinladyLOGO-2.png"
            alt="Chikin Lady"
            style={{
              height: '1.5em',
              width: 'auto',
              verticalAlign: 'middle',
              marginRight: '0.5rem',
              marginLeft: '0.5rem'
            }}
          />
        </SectionTitle>

        <motion.div
          style={{ textAlign: 'center', marginBottom: '2rem' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <motion.a
            href="https://nftarcade.chikinlady.com/"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'inline-block',
              background: 'linear-gradient(45deg, #6bcf7f, #4ecdc4)',
              color: 'white',
              textDecoration: 'none',
              padding: '1rem 2rem',
              borderRadius: '30px',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              fontFamily: "'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif",
              boxShadow: '0 6px 20px rgba(107, 207, 127, 0.4)',
              transition: 'all 0.3s ease'
            }}
          >
            NFT ARCADE
          </motion.a>
          <div style={{
            fontSize: '0.95rem',
            color: '#666',
            marginTop: '0.75rem',
            fontWeight: 'bold',
            fontFamily: 'Comic Sans MS, Comic Neue, Fredoka One, Bubblegum Sans, Chalkboard SE, Marker Felt, Arial Rounded MT Bold, Helvetica Rounded, Arial, sans-serif'
          }}>
            NFT Holders may receive digital rewards at random (Which may include audio, video or art NFT's etc..)
          </div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <ScrollContainer>
            <motion.button
              className="left"
              onClick={scrollLeft}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '2px solid #ff6b6b',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                fontSize: '1.5rem',
                color: '#ff6b6b',
                left: '-25px'
              }}
            >
              ←
            </motion.button>
            <ProductsGrid ref={scrollContainerRef}>
              {products.map((product, index) => (
                <ProductCard
                  key={product.id}
                  variants={cardVariants}
                  whileHover={{
                    scale: 1.05,
                    rotateY: 5
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ProductImage
                    whileHover={{
                      scale: 1.1
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <img src={product.image} alt={product.name} />
                  </ProductImage>

                  <ProductTitle>{product.name}</ProductTitle>
                  <ProductDescription>{product.description}</ProductDescription>

                  <ShopButton
                    href={product.link || "https://thirdweb.com/base/0xC04530534be54e542689e7CD1d70961E2aDDD337"}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShopClick();
                    }}
                  >
                    {product.buttonLabel || "Purchase"}
                  </ShopButton>
                </ProductCard>
              ))}
            </ProductsGrid>
            <motion.button
              className="right"
              onClick={scrollRight}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '2px solid #ff6b6b',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                fontSize: '1.5rem',
                color: '#ff6b6b',
                right: '-25px'
              }}
            >
              →
            </motion.button>
          </ScrollContainer>
          <ScrollHint>
            💡 Click arrows or swipe to see more NFTs
          </ScrollHint>
        </motion.div>
      </Container>

      <RaffleForm
        isOpen={raffleFormOpen}
        onClose={() => setRaffleFormOpen(false)}
      />

      {/* aCAPTCHA Modal */}
      <AnimatePresence>
        {captchaOpen && (
          <CaptchaOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CaptchaContainer
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <CloseButton
                onClick={handleCaptchaClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                ✕
              </CloseButton>

              <CaptchaTitle
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                🛡️ Security Verification
              </CaptchaTitle>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {!useFallbackCaptcha ? (
                  <>
                    <p style={{
                      color: 'white',
                      marginBottom: '1.5rem',
                      textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
                      fontSize: '1rem'
                    }}>
                      Please complete the security verification to continue:
                    </p>

                    <captcha-element
                      id="airdrop-captcha"
                      type="visual"
                      difficulty="easy"
                      style={{
                        width: '100%',
                        minHeight: '200px',
                        display: 'block'
                      }}
                    />
                  </>
                ) : (
                  <>
                    <p style={{
                      color: 'white',
                      marginBottom: '1rem',
                      textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
                      fontSize: '1rem'
                    }}>
                      Please enter the code below to continue:
                    </p>

                    <div style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      color: '#333',
                      padding: '1rem',
                      borderRadius: '10px',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      margin: '1rem 0',
                      letterSpacing: '2px',
                      fontFamily: 'Courier New, monospace'
                    }}>
                      {captchaText}
                    </div>

                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      placeholder="Enter the code above"
                      maxLength={5}
                      autoComplete="off"
                      style={{
                        width: '100%',
                        padding: '0.8rem 1rem',
                        border: captchaError ? '2px solid #ff6b6b' : 'none',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        background: 'rgba(255, 255, 255, 0.9)',
                        color: '#333',
                        textAlign: 'center',
                        fontFamily: 'Courier New, monospace',
                        fontWeight: 'bold',
                        letterSpacing: '1px',
                        margin: '1rem 0'
                      }}
                    />

                    {captchaError && (
                      <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                          color: '#ff6b6b',
                          fontSize: '0.9rem',
                          margin: '0.5rem 0',
                          fontWeight: 'bold',
                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                        ❌ Incorrect code. Please try again.
                      </motion.p>
                    )}

                    <div style={{ marginTop: '1rem' }}>
                      <motion.button
                        onClick={handleFallbackCaptchaSubmit}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={!userInput.trim()}
                        style={{
                          background: 'linear-gradient(45deg, #6bcf7f, #4ecdc4)',
                          color: 'white',
                          border: 'none',
                          padding: '0.8rem 1.5rem',
                          borderRadius: '25px',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          cursor: 'pointer',
                          margin: '0.5rem',
                          transition: 'all 0.3s ease',
                          opacity: !userInput.trim() ? 0.6 : 1
                        }}
                      >
                        Verify & Continue
                      </motion.button>

                      <motion.button
                        onClick={() => {
                          const newCaptcha = generateCaptcha();
                          setCaptchaText(newCaptcha);
                          setUserInput('');
                          setCaptchaError(false);
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          background: 'linear-gradient(45deg, #ff6b6b, #ffd93d)',
                          color: 'white',
                          border: 'none',
                          padding: '0.8rem 1.5rem',
                          borderRadius: '25px',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          cursor: 'pointer',
                          margin: '0.5rem',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        🔄 New Code
                      </motion.button>
                    </div>
                  </>
                )}
              </motion.div>
            </CaptchaContainer>
          </CaptchaOverlay>
        )}
      </AnimatePresence>
    </ProductsSection>
  );
};

export default Products;
